import { useEffect, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { LifeBuoy, Plus } from 'lucide-react';
import {
  NewSupportTicketForm,
  SupportTicketList,
  SupportTicketThread,
  markSupportTicketSeen,
  unseenSupportReplyIds,
  useToast,
  type NewSupportTicketInput,
} from '@mustauto/ui/web';
import {
  useRefreshSupportBadge,
  useSupportTicket,
  useSupportTickets,
} from '@/hooks/useSupportTickets';

export function SupportPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [creating, setCreating] = useState(false);
  const { tickets, isLoading, isError, refetch, createTicket, isCreating } = useSupportTickets();

  const handleCreate = async (input: NewSupportTicketInput) => {
    try {
      const { ticketId } = await createTicket(input);
      setCreating(false);
      toast({ type: 'success', title: 'Chamado aberto', description: 'A equipe responde por aqui e por e-mail.' });
      navigate({ to: '/support/$ticketId', params: { ticketId } });
    } catch {
      toast({ type: 'error', title: 'Não foi possível abrir o chamado', description: 'Tente novamente em instantes.' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
            <LifeBuoy size={24} />
            Suporte
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Fale com a equipe MustAuto e acompanhe as respostas por aqui.
          </p>
        </div>
        {!creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            <Plus size={16} />
            Novo chamado
          </button>
        )}
      </div>

      {creating && (
        <NewSupportTicketForm
          onSubmit={handleCreate}
          onCancel={() => setCreating(false)}
          isSubmitting={isCreating}
        />
      )}

      <SupportTicketList
        tickets={tickets}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        onOpen={(ticketId) => navigate({ to: '/support/$ticketId', params: { ticketId } })}
        onNew={() => setCreating(true)}
        unseenIds={unseenSupportReplyIds(tickets)}
      />
    </div>
  );
}

export function SupportTicketPage() {
  const { ticketId } = useParams({ strict: false }) as { ticketId: string };
  const navigate = useNavigate();
  const { toast } = useToast();
  const refreshBadge = useRefreshSupportBadge();
  const { ticket, isLoading, isError, refetch, reply, isReplying } = useSupportTicket(ticketId);

  // Abrir a thread marca as respostas da equipe como vistas neste navegador
  // (não há estado de leitura no servidor) e recalcula o badge da sidebar.
  useEffect(() => {
    if (!ticket) return;
    const lastStaffReply = [...ticket.messages].reverse().find((m) => m.fromStaff);
    if (!lastStaffReply) return;
    markSupportTicketSeen(ticket.id, lastStaffReply.createdAt);
    refreshBadge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id, ticket?.messages.length]);

  const handleSend = async (content: string) => {
    try {
      await reply(content);
    } catch {
      toast({ type: 'error', title: 'Não foi possível enviar', description: 'Tente novamente em instantes.' });
    }
  };

  return (
    <SupportTicketThread
      ticket={ticket}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => void refetch()}
      onBack={() => navigate({ to: '/support' })}
      onSend={handleSend}
      isSending={isReplying}
    />
  );
}
